import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Card, Col, Collapse, Row, Space, Tag, Typography } from 'antd'
import {
  BankOutlined,
  HomeOutlined,
  IdcardOutlined,
  MailOutlined,
  ShopOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { IntegrationIcons } from '../integration-icons'
import {
  InviteButton,
  employeeInitials,
  inactiveOpacityStyle,
  inviteStatusLabel,
  sortActiveFirst,
} from './shared'
import type { CompanyStructureViewProps, Employee, Office, Organization } from './types'

const { Text, Paragraph } = Typography

function EmployeeCard({ employee, muted }: { employee: Employee; muted?: boolean }) {
  const dimmed = muted ?? !employee.is_active
  return (
    <Card
      size="small"
      hoverable
      styles={{ body: { padding: 14 } }}
      style={{
        height: '100%',
        borderRadius: 10,
        borderColor: '#f0f0f0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        ...(dimmed ? inactiveOpacityStyle : null),
      }}
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Space size={12} align="start" style={{ width: '100%' }}>
          <Avatar
            size={40}
            style={{ background: '#1677ff', flexShrink: 0 }}
            icon={!employee.name ? <TeamOutlined /> : undefined}
          >
            {employee.name ? employeeInitials(employee) : null}
          </Avatar>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Link to={`/employees_new/${employee.id}`}>
              <Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>
                {employee.name || employee.login}
              </Text>
            </Link>
            <div style={{ marginTop: 2 }}>{inviteStatusLabel(employee)}</div>
            <div style={{ marginTop: 6 }}>
              <IntegrationIcons
                integrations={employee.integrations}
                size={14}
                services={['mango']}
              />
            </div>
          </div>
        </Space>

        <div
          style={{
            background: '#fafafa',
            borderRadius: 8,
            padding: '8px 10px',
          }}
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space size={6}>
              <MailOutlined style={{ color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {employee.login}
              </Text>
            </Space>
            <Space size={6} align="start">
              <IdcardOutlined style={{ color: '#8c8c8c', marginTop: 3 }} />
              <Space size={[4, 4]} wrap>
                {employee.roles.length > 0 ? (
                  employee.roles.map(r => (
                    <Tag key={r.id} style={{ margin: 0 }}>
                      {r.name}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Роли не назначены
                  </Text>
                )}
              </Space>
            </Space>
          </Space>
        </div>
      </Space>
    </Card>
  )
}

function OfficeBlock({
  org,
  office,
  onInvite,
  parentInactive = false,
}: {
  org: Organization
  office: Office
  onInvite: CompanyStructureViewProps['onInvite']
  parentInactive?: boolean
}) {
  const employees = useMemo(() => sortActiveFirst(office.employees), [office.employees])
  const dimSelf = !parentInactive && !office.is_active
  const dimEmployees = !parentInactive && office.is_active

  return (
    <div style={dimSelf ? inactiveOpacityStyle : undefined}>
      <Collapse
        size="small"
        ghost
        defaultActiveKey={[`office-${office.id}`]}
        items={[
          {
            key: `office-${office.id}`,
            label: (
              <Space size={8} wrap>
                <HomeOutlined />
                <Link to={`/office/${office.id}`} onClick={e => e.stopPropagation()}>
                  <Text strong>{office.name}</Text>
                </Link>
                {!office.is_active && <Tag>Неактивен</Tag>}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {office.employees.length} сотр.
                </Text>
                <IntegrationIcons integrations={office.integrations} size={16} />
              </Space>
            ),
            extra: office.is_active ? (
              <Space size={8} onClick={e => e.stopPropagation()}>
                <InviteButton org={org} office={office} onInvite={onInvite} />
              </Space>
            ) : undefined,
            children:
              employees.length === 0 ? (
                <Text type="secondary">Сотрудников пока нет</Text>
              ) : (
                <Row gutter={[12, 12]}>
                  {employees.map(emp => (
                    <Col key={emp.id} xs={24} sm={12} xl={8}>
                      <EmployeeCard employee={emp} muted={dimEmployees ? !emp.is_active : false} />
                    </Col>
                  ))}
                </Row>
              ),
          },
        ]}
      />
    </div>
  )
}

function OrganizationBlock({
  org,
  onInvite,
}: {
  org: Organization
  onInvite: CompanyStructureViewProps['onInvite']
}) {
  const offices = useMemo(() => sortActiveFirst(org.offices), [org.offices])

  return (
    <div style={!org.is_active ? inactiveOpacityStyle : undefined}>
      <Collapse
        size="small"
        defaultActiveKey={[`org-${org.id}`]}
        style={{ marginBottom: 8, background: '#fff' }}
        items={[
          {
            key: `org-${org.id}`,
            label: (
              <Space size={8} wrap>
                <BankOutlined />
                <Link to={`/new/organization/${org.id}`} onClick={e => e.stopPropagation()}>
                  <Text strong>{org.name}</Text>
                </Link>
                {!org.is_active && <Tag>Неактивна</Tag>}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {org.offices.length} офис.
                </Text>
              </Space>
            ),
            children:
              offices.length === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  В вашей организации ещё нет офисов.{' '}
                  <Link to={`/office/${org.id}/new`}>Добавить офис</Link>
                </Paragraph>
              ) : (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {offices.map(office => (
                    <OfficeBlock
                      key={office.id}
                      org={org}
                      office={office}
                      onInvite={onInvite}
                      parentInactive={!org.is_active}
                    />
                  ))}
                </Space>
              ),
          },
        ]}
      />
    </div>
  )
}

/** Текущая реализация на /home — эталон для сравнения. */
export function CompanyStructureCurrent({ structure, onInvite }: CompanyStructureViewProps) {
  const brandItems = structure.brands
    .filter(brand => brand.organizations.length > 0)
    .map(brand => {
      const organizations = sortActiveFirst(brand.organizations)
      return {
        key: `brand-${brand.id}`,
        label: (
          <Space size={8}>
            <ShopOutlined />
            <Text strong>Бренд: {brand.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {brand.organizations.length} орг.
            </Text>
          </Space>
        ),
        children: (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {organizations.map(org => (
              <OrganizationBlock key={org.id} org={org} onInvite={onInvite} />
            ))}
          </Space>
        ),
      }
    })

  const orgsWithoutBrand = useMemo(
    () => sortActiveFirst(structure.organizations_without_brand),
    [structure.organizations_without_brand],
  )

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {brandItems.length > 0 && (
        <Collapse size="small" defaultActiveKey={brandItems.map(i => i.key)} items={brandItems} />
      )}
      {orgsWithoutBrand.map(org => (
        <OrganizationBlock key={org.id} org={org} onInvite={onInvite} />
      ))}
    </Space>
  )
}
